import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translate } from '../data/translations';
import type { Language } from '../types/lead';
interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}
const LanguageContext = createContext<LanguageContextValue | null>(null);
const STORAGE_KEY = 'mcq_lang';
function detectInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'es';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'es') return stored;
  } catch {
    /* localStorage unavailable */
  }
  // Spanish is the default on first visit — the ES toggle renders active on load.
  // Browser detection is intentionally NOT used: this audience is Spanish-first and
  // many devices report an English locale.
  return 'es';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Language>(detectInitialLanguage);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.setAttribute('translate', 'no');
  }, [lang]);
  const setLang = useCallback((next: Language) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);
  const t = useCallback((key: string, vars?: Record<string, string>) => {
    let value = translate(key, lang);
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return value;
  }, [lang]);
  const value = useMemo(() => ({
    lang,
    setLang,
    t
  }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value} data-id="element-425" data-mp="2faig1kyit0wm">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}